/**
 * The pizza category, split into the sections legacy shows — FRONTEND.md §9.16.
 *
 * Legacy's `Home.tsx` renders five named groups and filters `p.pizzaSubcategory === sc` for each,
 * skipping any that is empty. The AGOS menu rendered one flat grid, so 29 pizzas arrived as an
 * undifferentiated wall where the old site showed five labelled groups. The field was ported; the
 * screen that reads it was not.
 */

/**
 * Legacy's order, and deliberately a fixed list rather than whatever the data holds.
 *
 * Sorting these alphabetically or by size would reorder a menu customers already know. `Klasické`
 * first is a choice somebody made, and it is not this port's to revisit.
 */
// §8/§14.2 asks that user-visible Slovak go through `tt()`. These are not user-visible strings: they
// are **data keys**, compared byte-for-byte against `products.pizza_subcategory`, and `tt()` would
// return the German or English rendering under any other language and match nothing. The heading a
// customer reads is translated where it is rendered — `<T>{section.title}</T>` in HomePage — so the
// display goes through the workflow and the key does not, which is the distinction the rule's own
// note draws between the two.
/* eslint-disable no-restricted-syntax */
export const PIZZA_SUBCATEGORIES = [
  'Klasické',
  'Slovenské',
  'Talianské',
  'Pikantné',
  'Špeciálne',
] as const
/* eslint-enable no-restricted-syntax */

/** A product, as much of one as this needs. */
export interface SectionableProduct {
  pizzaSubcategory?: string | null
}

export interface PizzaSection<T> {
  /** The heading, or `null` for the products that carry no sub-category. */
  title: string | null
  products: T[]
}

/**
 * The products grouped into sections, in legacy's order, empty sections dropped.
 *
 * Three rules, and the third is where this differs from legacy on purpose:
 *
 *  1. the five known sub-categories come first, in {@link PIZZA_SUBCATEGORIES} order;
 *  2. anything with no sub-category goes last, in one untitled section — 135 products on this data;
 *  3. a sub-category that is in the data but not in the list is rendered **after** the five, under
 *     its own name. Legacy filters by an exact match against its fixed array, so such a product
 *     disappears from the menu entirely. Reordering is a preference; dropping a product a restaurant
 *     is trying to sell is not, and that is the one behaviour here not worth preserving.
 */
export function pizzaSections<T extends SectionableProduct>(products: readonly T[]): PizzaSection<T>[] {
  const known = new Set<string>(PIZZA_SUBCATEGORIES)
  const sections: PizzaSection<T>[] = []

  for (const title of PIZZA_SUBCATEGORIES) {
    const group = products.filter((product) => nameOf(product) === title)
    if (group.length > 0) sections.push({ title, products: group })
  }

  // Unknown names, in the order they first appear — there is no authored order to follow, and first
  // appearance at least matches the product list a menu editor sees.
  const seen = new Set<string>()
  for (const product of products) {
    const name = nameOf(product)
    if (name === null || known.has(name) || seen.has(name)) continue
    seen.add(name)
    sections.push({ title: name, products: products.filter((other) => nameOf(other) === name) })
  }

  const ungrouped = products.filter((product) => nameOf(product) === null)
  if (ungrouped.length > 0) sections.push({ title: null, products: ungrouped })

  return sections
}

/** `null` for absent, empty or whitespace — all three mean *no sub-category* on this data. */
function nameOf(product: SectionableProduct): string | null {
  const raw = product.pizzaSubcategory
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed === '' ? null : trimmed
}
